// Talks to the backend's REST /annotations endpoints, mounted under the API
// root (server/src/http/mount.ts): /api/annotations for the app, and the same
// read-only route under a share's root.

import { AnnotationWriteError, type AnnotationStore } from '../AnnotationStore';
import type { Annotation, AnnotationPatch, NewAnnotation } from '../types';
import { getConfig } from '../../config/runtimeConfig';
import { HttpError } from '../../lib/httpError';

/**
 * Send the session cookie, on every one of these.
 *
 * None of them did. The annotations API sits beside /dicom-web on the SERVER's
 * origin, which is not the page's — :8043 and :3004 in development — so fetch's
 * default of `same-origin` silently dropped the cookie and every call 401'd.
 * Opening a slide loads its annotations, so the viewer answered "you are no
 * longer signed in" the moment you opened one.
 *
 * Exactly the bug the upload client had, in the third client nobody thought to
 * check: the DICOMweb tiles carry credentials through a requestHook and the
 * upload and workspace clients set this explicitly, so annotations were the one
 * surface left. An audit for the pattern missed it because it builds its URL
 * from `this.base` rather than the shared helpers the others use.
 *
 * Conditional for the same reason as everywhere else: wildcard CORS and
 * credentialed requests are mutually exclusive, and AUTH_MODE=off sends the
 * wildcard.
 */
const creds = (): RequestCredentials => (getConfig().auth ? 'include' : 'same-origin');

/** The server's reason for refusing a write, when it gave one. */
async function refusal(res: Response, what: string): Promise<AnnotationWriteError> {
  const body = (await res.json().catch(() => ({}))) as { error?: unknown; code?: unknown };
  const message = typeof body.error === 'string' && body.error ? body.error : `${what} failed: ${res.status}`;
  return new AnnotationWriteError(message, res.status, typeof body.code === 'string' ? body.code : undefined);
}

export class HttpAnnotationStore implements AnnotationStore {
  private readonly base: string;

  /** `apiRoot`: where /annotations lives — the app's API root, or a share's
   *  root under it. */
  constructor(apiRoot: string) {
    this.base = `${apiRoot.replace(/\/$/, '')}/annotations`;
  }

  async list(studyId: string, seriesInstanceUID?: string): Promise<Annotation[]> {
    const params = new URLSearchParams({ study: studyId });
    if (seriesInstanceUID) params.set('series', seriesInstanceUID);
    const res = await fetch(`${this.base}?${params.toString()}`, { credentials: creds() });
    if (!res.ok) throw new HttpError(`list annotations failed: ${res.status}`, res.status);
    return res.json();
  }

  async create(annotation: NewAnnotation): Promise<Annotation> {
    const res = await fetch(this.base, {
      method: 'POST',
      credentials: creds(),
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(annotation),
    });
    if (!res.ok) throw await refusal(res, 'create annotation');
    return res.json();
  }

  async update(id: string, patch: AnnotationPatch): Promise<Annotation> {
    const res = await fetch(`${this.base}/${id}`, {
      method: 'PATCH',
      credentials: creds(),
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw await refusal(res, 'update annotation');
    return res.json();
  }

  async remove(id: string): Promise<void> {
    const res = await fetch(`${this.base}/${id}`, { method: 'DELETE', credentials: creds() });
    if (!res.ok && res.status !== 404) throw await refusal(res, 'delete annotation');
  }
}
